// apps/admin-ui/src/components/widgets/AssetHoldings.tsx
// Segregated asset holdings widget for custodians
// ALL RIGHTS RESERVED

import '../styles/widgets.css';

export interface AssetHoldingsProps {
  data?: {
    custodian: string;
    lastUpdated: string;
    accounts: {
      id: string;
      name: string;
      type: 'operating' | 'reserve' | 'segregated';
      balance: number;
      currency: string;
      status: 'active' | 'restricted' | 'frozen';
    }[];
    totalHoldings: number;
  };
}

export function AssetHoldings({ data }: AssetHoldingsProps) {
  // Mock data for development
  const holdingsData = data || {
    custodian: 'BNY Mellon',
    lastUpdated: '2026-02-01T14:30:00Z',
    accounts: [
      { id: 'ACC-001', name: 'CXXR Reserve - Primary', type: 'reserve' as const, balance: 75_000_000, currency: 'USD', status: 'active' as const },
      { id: 'ACC-002', name: 'CXXR Reserve - T-Bills', type: 'reserve' as const, balance: 37_500_000, currency: 'USD', status: 'active' as const },
      { id: 'ACC-003', name: 'CXXR Segregated - Client A', type: 'segregated' as const, balance: 5_000_000, currency: 'USD', status: 'active' as const },
      { id: 'ACC-004', name: 'CXXR Segregated - Client B', type: 'segregated' as const, balance: 3_500_000, currency: 'USD', status: 'active' as const },
      { id: 'ACC-005', name: 'CXXR Operating', type: 'operating' as const, balance: 4_000_000, currency: 'USD', status: 'active' as const },
    ],
    totalHoldings: 125_000_000,
  };

  const typeLabels = {
    operating: 'Operating',
    reserve: 'Reserve',
    segregated: 'Segregated',
  };

  const typeColors = {
    operating: 'var(--color-text-muted)',
    reserve: 'var(--color-primary)',
    segregated: 'var(--color-warning)',
  };

  const statusIcons = {
    active: '●',
    restricted: '◐',
    frozen: '○',
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);

  const formatDateTime = (dateStr: string) =>
    new Date(dateStr).toLocaleString('en-US', {
      month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });

  // Group by type
  const reserveAccounts = holdingsData.accounts.filter(a => a.type === 'reserve');
  const segregatedAccounts = holdingsData.accounts.filter(a => a.type === 'segregated');
  const operatingAccounts = holdingsData.accounts.filter(a => a.type === 'operating');

  const reserveTotal = reserveAccounts.reduce((sum, a) => sum + a.balance, 0);
  const segregatedTotal = segregatedAccounts.reduce((sum, a) => sum + a.balance, 0);
  const operatingTotal = operatingAccounts.reduce((sum, a) => sum + a.balance, 0);

  return (
    <div className="widget-asset-holdings">
      <div className="holdings-header">
        <div className="custodian-info">
          <span className="custodian-name">{holdingsData.custodian}</span>
          <span className="custodian-updated">
            Updated: {formatDateTime(holdingsData.lastUpdated)}
          </span>
        </div>
        <div className="total-holdings">
          <span className="total-label">Total Holdings</span>
          <span className="total-value">{formatCurrency(holdingsData.totalHoldings)}</span>
        </div>
      </div>

      <div className="holdings-summary">
        <div className="summary-item">
          <span className="summary-label" style={{ color: typeColors.reserve }}>Reserve</span>
          <span className="summary-value">{formatCurrency(reserveTotal)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label" style={{ color: typeColors.segregated }}>Segregated</span>
          <span className="summary-value">{formatCurrency(segregatedTotal)}</span>
        </div>
        <div className="summary-item">
          <span className="summary-label" style={{ color: typeColors.operating }}>Operating</span>
          <span className="summary-value">{formatCurrency(operatingTotal)}</span>
        </div>
      </div>

      <table className="holdings-table">
        <thead>
          <tr>
            <th>Account</th>
            <th>Type</th>
            <th>Balance</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>
          {holdingsData.accounts.map((account) => (
            <tr key={account.id}>
              <td>
                <span className="account-name">{account.name}</span>
                <span className="account-id">{account.id}</span>
              </td>
              <td>
                <span
                  className="account-type"
                  style={{ color: typeColors[account.type] }}
                >
                  {typeLabels[account.type]}
                </span>
              </td>
              <td className="account-balance">{formatCurrency(account.balance)}</td>
              <td className={`account-status status-${account.status}`}>
                {statusIcons[account.status]} {account.status}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default AssetHoldings;
